import { doc, getDoc } from 'firebase/firestore';
import { Firestore } from 'firebase/firestore';
import { normalizeAcademicYear } from '../utils/courseIdentity';
import AsyncStorage from '@react-native-async-storage/async-storage';

export type SyllabusCatalog = {
  ids: string[];
  count: number;
  checksum: string;
  syncVersion: string;
  updatedAt?: unknown;
};

export const catalogChecksum = (ids: string[]): string => {
  const value = [...ids].sort().join('\n');
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

export const fetchSyllabusCatalog = async (db: Firestore, academicYear: string): Promise<SyllabusCatalog | null> => {
  const year = academicYear === 'all' ? 'all' : normalizeAcademicYear(academicYear);
  if (!year) return null;
  const cacheKey = `syllabusCatalog:${year}`;
  const cached = await AsyncStorage.getItem(cacheKey);
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as SyllabusCatalog & { cachedAt: number };
      if (Date.now() - parsed.cachedAt < 24 * 60 * 60 * 1000 && parsed.count === parsed.ids.length && parsed.checksum === catalogChecksum(parsed.ids)) return parsed;
    } catch { /* discard malformed persistent cache */ }
  }
  const snapshot = await getDoc(doc(db, 'syllabusCatalog', year));
  if (!snapshot.exists()) return null;
  const data = snapshot.data() as Partial<SyllabusCatalog>;
  const ids = Array.isArray(data.ids) ? data.ids.filter((id): id is string => typeof id === 'string') : [];
  const catalog = { ...data, ids, count: Number(data.count), checksum: String(data.checksum || ''), syncVersion: String(data.syncVersion || '') } as SyllabusCatalog;
  if (catalog.count !== ids.length || catalog.checksum !== catalogChecksum(ids)) return null;
  await AsyncStorage.setItem(cacheKey, JSON.stringify({ ...catalog, cachedAt: Date.now() }));
  return catalog;
};
